import {format} from 'date-fns';
import React, {useState} from 'react';

import {TableSkeleton} from '@/components';
import {useAttributionData} from '@/hooks/useAttributionData';
import {AttributionEventType} from '@/types/attribution';

import AppUsageHistoryTable from './AppUsageHistoryTable';

// Define card colors for each event type
const eventColors: Record<AttributionEventType, string> = {
  [AttributionEventType.INSTALL]: 'bg-blue-100 dark:bg-blue-900',
  [AttributionEventType.REINSTALL]: 'bg-purple-100 dark:bg-purple-900',
  [AttributionEventType.UNINSTALL]: 'bg-red-100 dark:bg-red-900',
  [AttributionEventType.FIRST_OPEN]: 'bg-green-100 dark:bg-green-900',
  [AttributionEventType.APP_REMOVE]: 'bg-yellow-100 dark:bg-yellow-900',
};

const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;

const ATTRIBUTION_EVENT_TYPES = [
  AttributionEventType.INSTALL,
  AttributionEventType.REINSTALL,
  AttributionEventType.UNINSTALL,
  AttributionEventType.FIRST_OPEN,
  AttributionEventType.APP_REMOVE,
];

const AttributionMetrics: React.FC = () => {
  // Sample the clock once per mount instead of on every render, so the trailing
  // 30-day window stays fixed for the lifetime of the mounted component.
  const [dateRange] = useState(() => {
    const now = Date.now();
    return {
      startDate: format(new Date(now - THIRTY_DAYS_IN_MS), 'yyyy-MM-dd'),
      endDate: format(new Date(now), 'yyyy-MM-dd'),
    };
  });

  const {data, isLoading} = useAttributionData({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    eventTypes: ATTRIBUTION_EVENT_TYPES,
  });

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (!data) {
    return (
      <div className="text-gray-600 dark:text-gray-400">No data available</div>
    );
  }

  return (
    <div className="p-6 dark:bg-dark-inputBg">
      <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
        User Attribution Metrics
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {(Object.entries(data) as [AttributionEventType, number][]).map(
          ([eventType, count]) => (
            <div
              key={eventType}
              className={`flex flex-col p-4 rounded-lg border border-gray-200 dark:border-gray-700 ${
                eventColors[eventType]
              } transition-all duration-200 hover:shadow-lg`}
            >
              <h3 className="text-sm text-gray-600 dark:text-gray-300 mb-2 capitalize">
                {eventType.replace('_', ' ')}
              </h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {count.toLocaleString()}
              </p>
            </div>
          ),
        )}
      </div>

      <div className="mt-8">
        <AppUsageHistoryTable />
      </div>
    </div>
  );
};

export default AttributionMetrics;
